import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardDocumentIcon,
  FolderIcon,
  FunnelIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import DashboardLayout from "../components/DashboardLayout";
import Logo from "../components/Logo";
import StatusBadge from "../components/common/StatusBadge";
import WeaveLoader from "../components/common/WeaveLoader";
import { useToast } from "../contexts/ToastContext";
import { setLoaded, setLoadingProjects, setProjects } from "../redux/project/projectSlice";
import { store } from "../redux/store";
import { projectsService } from "../services/projects";
import { sitePath } from "../utils/urls";
import { STATUS_META } from "./site/seo/constants";

const BUILDING_STATUSES = new Set(["building", "staging"]);
const SEARCH_DEBOUNCE_MS = 200;
const PAGE_SIZE = 12;
const SORT_OPTIONS = [
  { value: "edited", label: "Last edited" },
  { value: "created", label: "Newest" },
  { value: "name", label: "A–Z" },
];
const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "building", label: "Building" },
  { value: "ready", label: "Ready" },
  { value: "live", label: "Live" },
  { value: "staging", label: "Staging" },
  { value: "paused", label: "Paused" },
  { value: "archived", label: "Archived" },
];

const projectPath = (project) => (
  project.is_archived ? sitePath(project, "settings/management/") : sitePath(project, "preview/")
);

const projectStage = (project) => {
  const hasPlan = project.has_site_plan
    || (project.site_plan && Object.keys(project.site_plan).length > 0);
  const isBuilding = BUILDING_STATUSES.has(project.status);
  let stage = { status: "draft", label: "Data", variant: "neutral" };
  if (project.table_count) stage = { status: "draft", label: "Plan", variant: "info" };
  if (hasPlan) stage = { status: "draft", label: "Draft", variant: "neutral" };
  if (isBuilding) stage = { status: "building", label: "Building", variant: "warning" };
  if (project.status === "ready") stage = { status: "ready", label: "Ready", variant: "success" };
  if (project.status === "live") stage = { status: "live", label: "Live", variant: "success" };
  // Follow-on CRs / deploys leave lifecycle status as ready/live; draft_activity drives the badge.
  if (project.draft_activity === "ready_for_review") {
    stage = { status: "awaiting_approval", label: "Ready for review", variant: "info" };
  }
  if (project.draft_activity === "updating") {
    stage = { status: "building", label: isBuilding ? "Building" : "Updating", variant: "warning" };
  }
  if (project.draft_activity === "deploying") {
    stage = { status: "building", label: "Deploying", variant: "warning" };
  }
  if (project.is_archived) stage = { status: "archived", label: "Archived", variant: "neutral" };
  return stage;
};

const Home = ({ currentUser }) => {
  const { success } = useToast();
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.project.projects);
  const loaded = useSelector((state) => state.project.loaded);
  const loadingProjects = useSelector((state) => state.project.loadingProjects);
  const [searchParams, setSearchParams] = useSearchParams();
  const [shown, setShown] = useState(PAGE_SIZE);
  const [viewMode, setViewMode] = useState("grid");
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  // Default: hide archived (archived=false). Include-archived omits the param.
  const [includeArchived, setIncludeArchived] = useState(
    searchParams.has("archived") ? searchParams.get("archived") !== "false" : false,
  );
  const sortParam = searchParams.get("sort");
  const [sort, setSort] = useState(
    SORT_OPTIONS.some((o) => o.value === sortParam) ? sortParam : "edited",
  );
  const statusParam = searchParams.get("status") || "";
  const [statusFilter, setStatusFilter] = useState(
    STATUS_OPTIONS.some((o) => o.value === statusParam) ? statusParam : "",
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const loadMoreRef = useRef(null);
  const activeFilterCount =
    (includeArchived ? 1 : 0) + (sort !== "edited" ? 1 : 0) + (statusFilter ? 1 : 0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set("search", debouncedSearch);
    else next.delete("search");
    if (includeArchived) next.delete("archived");
    else next.set("archived", "false");
    if (sort && sort !== "edited") next.set("sort", sort);
    else next.delete("sort");
    if (statusFilter) next.set("status", statusFilter);
    else next.delete("status");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, includeArchived, sort, statusFilter]);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onDoc = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [filterOpen]);

  const refreshProjects = useCallback((showLoading) => {
    if (store.getState().project.loadingProjects) return;
    if (showLoading) dispatch(setLoadingProjects(true));
    projectsService.listProjects(
      { page_size: 500 },
      (response) => {
        const data = response.data;
        const list = data?.results || (Array.isArray(data) ? data : []);
        dispatch(setProjects(list));
        dispatch(setLoaded(true));
        dispatch(setLoadingProjects(false));
      },
      () => {
        dispatch(setLoaded(true));
        dispatch(setLoadingProjects(false));
      },
    );
  }, [dispatch]);

  const filteredProjects = useMemo(() => {
    const query = debouncedSearch.toLowerCase();
    const list = projects.filter((project) => {
      if (!includeArchived && project.is_archived) return false;
      if (statusFilter && project.status !== statusFilter) return false;
      if (!query) return true;
      return [project.name, project.subdomain, project.custom_domain].some(
        (value) => (value || "").toLowerCase().includes(query),
      );
    });
    return [...list].sort((a, b) => {
      if (sort === "name") {
        return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      }
      if (sort === "created") return new Date(b.create_date) - new Date(a.create_date);
      const aEdited = new Date(a.last_edit_at || a.modify_date || 0);
      const bEdited = new Date(b.last_edit_at || b.modify_date || 0);
      return bEdited - aEdited;
    });
  }, [projects, includeArchived, statusFilter, debouncedSearch, sort]);

  const visibleProjects = filteredProjects.slice(0, shown);
  const hasMore = filteredProjects.length > shown;
  const totalCount = filteredProjects.length;

  const needsPoll = projects.some(
    (project) => (
      !project.is_archived
      && (
        BUILDING_STATUSES.has(project.status)
        || project.draft_activity === "updating"
        || project.draft_activity === "deploying"
      )
    ),
  );

  useEffect(() => {
    setShown(PAGE_SIZE);
  }, [debouncedSearch, includeArchived, sort, statusFilter]);

  // Client-side pagination: when Load more is in view, reveal the next batch.
  // Re-bind when `shown` changes so a still-visible trigger keeps loading.
  useEffect(() => {
    if (!hasMore) return undefined;
    const el = loadMoreRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setShown((count) => count + PAGE_SIZE);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, shown]);

  useEffect(() => {
    refreshProjects(true);
  }, [refreshProjects]);

  useEffect(() => {
    if (!needsPoll) return undefined;
    const timer = setInterval(() => refreshProjects(false), 3000);
    return () => clearInterval(timer);
  }, [needsPoll, refreshProjects]);

  const copySiteUrl = async (event, url) => {
    event.stopPropagation();
    await navigator.clipboard.writeText(url);
    success("Copied", 2000);
  };

  // First-run empty only when nothing is narrowing the list (search/status/include-archived).
  const showCreateFirst = (
    loaded
    && !loadingProjects
    && totalCount === 0
    && !debouncedSearch
    && !statusFilter
    && !includeArchived
  );

  return (
    <DashboardLayout
      title="Your sites"
      currentUser={currentUser}
      primaryAction={(
        <Link
          to="/onboarding/"
          className="rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-semibold text-theme-button lg:px-4 lg:py-2 lg:text-sm"
        >
          Create new site
        </Link>
      )}
    >
      {showCreateFirst ? (
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
          <Logo className="mb-6 h-16 w-[166px]" />
          <h2 className="text-lg font-bold text-theme-text">Create your first site</h2>
          <p className="mt-1.5 max-w-md text-[13px] leading-6 text-theme-text opacity-60">
            Upload a CSV or pick a public dataset, we'll turn it into a live,
            search-optimized site. Free to start.
          </p>
          <Link
            to="/onboarding/"
            className="mt-5 inline-block rounded-lg bg-theme-primary px-6 py-2.5 text-sm font-semibold text-theme-button"
          >
            Create new site
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <h2 className="text-base font-semibold text-theme-text">Sites</h2>
              {loadingProjects ? (
                <span
                  className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-text opacity-50"
                  aria-label="Loading"
                />
              ) : (
                <span className="text-sm text-theme-text opacity-50">({totalCount})</span>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <label className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-theme-text opacity-40" />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search"
                  className="w-40 rounded-lg border border-theme-secondary bg-theme-main py-1.5 pl-8 pr-2.5 text-sm text-theme-text sm:w-48"
                />
              </label>
              <div className="relative" ref={filterRef}>
                <button
                  type="button"
                  onClick={() => setFilterOpen((v) => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-2.5 py-1.5 text-xs font-semibold transition ${
                    filterOpen || activeFilterCount > 0
                      ? "bg-theme-secondary text-theme-primary"
                      : "text-theme-text opacity-60 hover:opacity-100"
                  }`}
                  title="Filters"
                >
                  <FunnelIcon className="h-3.5 w-3.5" />
                  Filters
                  {activeFilterCount > 0 ? <span>({activeFilterCount})</span> : null}
                </button>
                {filterOpen && (
                  <div className="absolute right-0 z-30 mt-1.5 w-56 rounded-xl border border-theme-secondary bg-theme-main p-3 shadow-xl">
                    <div className="space-y-3">
                      <label className="block">
                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide opacity-50">
                          Sort
                        </span>
                        <select
                          value={sort}
                          onChange={(e) => setSort(e.target.value)}
                          className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-xs text-theme-text focus:border-theme-primary focus:outline-none"
                        >
                          {SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide opacity-50">
                          Status
                        </span>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-xs text-theme-text focus:border-theme-primary focus:outline-none"
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value || "all"} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                      <div>
                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide opacity-50">
                          Archived
                        </span>
                        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-theme-secondary px-3 py-2 text-xs text-theme-text">
                          <input
                            type="checkbox"
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-theme-secondary"
                          />
                          Include archived
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="inline-flex rounded-lg border border-theme-secondary p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={`rounded-md p-1.5 ${viewMode === "grid" ? "bg-theme-secondary" : "opacity-50 hover:opacity-100"}`}
                  title="Card view"
                >
                  <Squares2X2Icon className="h-4 w-4 text-theme-text" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={`rounded-md p-1.5 ${viewMode === "list" ? "bg-theme-secondary" : "opacity-50 hover:opacity-100"}`}
                  title="List view"
                >
                  <ListBulletIcon className="h-4 w-4 text-theme-text" />
                </button>
              </div>
            </div>
          </div>

          {!loaded || (loadingProjects && !projects.length) ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <WeaveLoader size={72} text="Loading sites..." />
            </div>
          ) : visibleProjects.length === 0 ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
              <p className="text-sm font-semibold text-theme-text">No sites match</p>
              <p className="mt-1 max-w-sm text-xs text-theme-text opacity-60">
                {debouncedSearch
                  ? "Try a different search, or clear filters to see all sites."
                  : "Try changing or clearing your filters to see more sites."}
              </p>
              {(activeFilterCount > 0 || debouncedSearch) && (
                <button
                  type="button"
                  onClick={() => {
                    setStatusFilter("");
                    setSort("edited");
                    setIncludeArchived(false);
                    setSearchInput("");
                    setDebouncedSearch("");
                  }}
                  className="mt-4 text-xs font-semibold text-theme-primary hover:opacity-80"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : viewMode === "list" ? (
            <div className="divide-y divide-theme-secondary rounded-xl border border-theme-secondary">
              {visibleProjects.map((project) => (
                <SiteListRow
                  key={project.hash}
                  project={project}
                  onCopyUrl={copySiteUrl}
                />
              ))}
            </div>
          ) : (
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))" }}
            >
              {visibleProjects.map((project) => (
                <SiteCard
                  key={project.hash}
                  project={project}
                  onCopyUrl={copySiteUrl}
                />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                ref={loadMoreRef}
                type="button"
                onClick={() => setShown((count) => count + PAGE_SIZE)}
                className="rounded-lg border border-theme-secondary px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-5"
              >
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

const SiteCard = ({ project, onCopyUrl }) => {
  const siteUrl = project.custom_domain
    ? `https://${project.custom_domain}`
    : project.production_url || project.staging_url;
  const displaySiteUrl = siteUrl?.replace(/^https?:\/\//, "") || "Site address pending";
  const metaImageUrl = project.meta_image_url;
  const hasPreview = Boolean(metaImageUrl) && !project.is_archived;
  const isBuilding = BUILDING_STATUSES.has(project.status);
  const isUpdating = project.draft_activity === "updating";
  const isDeploying = project.draft_activity === "deploying";
  const stage = projectStage(project);
  const failCount = project.audit_counts?.fail || 0;
  const warnCount = project.audit_counts?.warn || 0;
  const hasAuditIssues = failCount > 0 || warnCount > 0;
  const href = projectPath(project);
  const headline = project.is_archived
    ? "This site is archived"
    : isDeploying
      ? (project.status_message || "Deploying your site")
      : isUpdating
        ? (project.status_message || (isBuilding ? "Building your site" : "Updating your site"))
        : isBuilding || project.status === "ready"
          ? (project.status_message || (project.status === "ready" ? "Ready to publish" : "Building your site"))
          : `${stage.label} stage`;

  return (
    <article className="overflow-hidden rounded-2xl border border-theme-secondary bg-theme-main transition hover:border-theme-primary">
      <div className="relative h-48 overflow-hidden border-b border-theme-secondary bg-theme-secondary">
        {hasPreview ? (
          <>
            <img
              src={metaImageUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute inset-0 bg-black/20" />
            <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
            <Link
              to={href}
              aria-label={`Open ${project.name} preview`}
              className="absolute inset-0"
            />
            <span className="pointer-events-none absolute bottom-3 left-4 flex gap-4 text-xs font-semibold text-white">
              <span>{project.table_count} tables</span>
              <span>{project.page_count || "—"} pages</span>
            </span>
          </>
        ) : (
          <Link
            to={href}
            className="absolute inset-0 flex items-center justify-center p-6 text-center"
          >
            <div className="absolute left-1/2 top-7 h-24 w-44 -translate-x-1/2 rounded-lg border border-theme-secondary bg-theme-main opacity-50">
              <div className="h-4 border-b border-theme-secondary" />
              <div className="mx-auto mt-4 h-2 w-20 rounded-full bg-theme-secondary" />
              <div className="mx-auto mt-2 h-2 w-28 rounded-full bg-theme-secondary" />
            </div>
            <div className="relative mt-24">
              <p className="text-sm font-semibold text-theme-text">
                {headline}
              </p>
              {!isBuilding && !isUpdating && !isDeploying && project.status !== "ready" && (
                <p className="mt-1 text-xs text-theme-text opacity-60">
                  {project.status_message || "Your site preview will appear here when it is ready."}
                </p>
              )}
              <p className="mt-2 text-[11px] font-semibold text-theme-text opacity-60">
                {project.table_count} tables · {project.page_count || "—"} pages
              </p>
            </div>
          </Link>
        )}
        <span className="absolute right-3 top-3 z-10">
          <StatusBadge {...stage} />
        </span>
      </div>

      <div className="flex items-center gap-3 p-[18px]">
        <div className="min-w-0 flex-1">
          <Link to={href} className="block w-full text-left">
            <h2 className="truncate text-[14.5px] font-bold text-theme-text">{project.name}</h2>
          </Link>
          <div className="mt-0.5 flex min-w-0 items-center gap-1">
            <Link
              to={href}
              className="min-w-0 truncate text-left text-xs text-theme-primary"
            >
              {displaySiteUrl}
            </Link>
            {siteUrl && (
              <button
                type="button"
                onClick={(event) => onCopyUrl(event, siteUrl)}
                title="Copy site URL"
                aria-label={`Copy ${project.name} URL`}
                className="shrink-0 rounded p-0.5 text-theme-text opacity-50 transition hover:bg-theme-secondary hover:opacity-100"
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2.5 text-xs text-theme-text">
          {hasAuditIssues && ["fail", "warn"].map((key) => {
            const meta = STATUS_META[key];
            const n = key === "fail" ? failCount : warnCount;
            if (n <= 0) return null;
            return (
              <Link
                key={key}
                to={sitePath(project, "seo/issues/")}
                className={`inline-flex items-center gap-0.5 ${meta.text} hover:opacity-80`}
                title={`View ${key === "fail" ? "failures" : "warnings"}`}
              >
                <meta.Icon className="h-3.5 w-3.5" />
                {n}
              </Link>
            );
          })}
        </div>
      </div>
    </article>
  );
};

const SiteListIcon = ({ project }) => {
  if (project?.favicon_url) {
    return (
      <img
        src={project.favicon_url}
        alt=""
        className="h-5 w-5 shrink-0 rounded object-cover"
      />
    );
  }
  return <FolderIcon className="h-5 w-5 shrink-0 text-theme-text opacity-40" aria-hidden="true" />;
};

const SiteListRow = ({ project, onCopyUrl }) => {
  const siteUrl = project.custom_domain
    ? `https://${project.custom_domain}`
    : project.production_url || project.staging_url;
  const displaySiteUrl = siteUrl?.replace(/^https?:\/\//, "") || "Site address pending";
  const stage = projectStage(project);
  const href = projectPath(project);

  return (
    <div className="flex items-center gap-3 px-4 py-3 transition hover:bg-black hover:bg-opacity-5">
      <Link to={href} className="shrink-0">
        <SiteListIcon project={project} />
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <Link to={href} className="min-w-0 truncate text-sm font-semibold text-theme-text">
            {project.name}
          </Link>
          <StatusBadge {...stage} />
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-1">
          <Link to={href} className="min-w-0 truncate text-xs text-theme-primary">
            {displaySiteUrl}
          </Link>
          {siteUrl && (
            <button
              type="button"
              onClick={(event) => onCopyUrl(event, siteUrl)}
              title="Copy site URL"
              aria-label={`Copy ${project.name} URL`}
              className="shrink-0 rounded p-0.5 text-theme-text opacity-50 transition hover:bg-theme-secondary hover:opacity-100"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 text-xs text-theme-text opacity-60">
        <span>{project.table_count} tables</span>
        <span>·</span>
        <span>{project.page_count || "—"} pages</span>
      </div>
    </div>
  );
};

export default Home;
